// Gradually migrate 50% of traffic to the new studio wizard.
// The assigned destination is persisted in localStorage so the user always
// lands on the same app across visits, avoiding inconsistent behaviour.
if (
  process.env.NODE_ENV === "production" &&
  window.location.hostname === "editor.klydoclock.com"
) {
  const REDIRECT_KEY = "klydoDestination";
  let destination = localStorage.getItem(REDIRECT_KEY);
  if (!destination) {
    destination = Math.random() < 0.5 ? "studio" : "editor";
    localStorage.setItem(REDIRECT_KEY, destination);
  }
  if (destination === "studio") {
    window.location.replace(
      window.location.href.replace(
        "editor.klydoclock.com",
        "studio.klydoclock.com",
      ),
    );
  }
}

import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { createTheme, ThemeProvider } from "@mui/material";
import { KLYDO_BLACK, KLYDO_YELLOW } from "./consts/colors";

const container = document.getElementById("root");
const root = createRoot(container);
const darkTheme = createTheme({
  typography: {
    fontFamily: [
      "Gilroy-bold, Gilroy-SemiBold, Gilroy-Medium, Gilroy-Regular, Gilroy-Light",
    ].join(","),
    button: {
      textTransform: "capitalize",
      fontFamily: "Gilroy-SemiBold",
      fontWeight: 500,
    },
    h3: {
      fontFamily: "Gilroy-Bold",
      lineHeight: "56px",
      fontSize: "48px",
      fontWeight: 400,
    },
    h5: {
      fontFamily: "Gilroy-Medium",
      fontSize: "24px",
      lineHeight: "32px",
      fontWeight: 400,
    },
    h6: {
      fontFamily: "Gilroy-SemiBold",
      fontSize: "19px",
      lineHeight: "24px",
    },
    subtitle1: {
      fontFamily: "Gilroy-Medium",
      fontSize: "16px",
      lineHeight: "28px",
      fontWeight: 400,
      color: "rgba(255, 255, 255, 0.7)",
    },
  },
  palette: {
    mode: "dark",
    primary: {
      main: KLYDO_YELLOW,
      contrastText: KLYDO_BLACK,
    },
    secondary: {
      main: "#212121",
      contrastText: "#626262",
    },
    background: {
      default: KLYDO_BLACK,
      paper: KLYDO_BLACK,
    },
  },
});
const environment =
  !process.env.NODE_ENV || process.env.NODE_ENV === "development"
    ? "development"
    : "production";
if (environment === "production") {
  Sentry.init({
    dsn: "https://ae4ef86504cb689b992e413f1acd9389@o4508608097681408.ingest.us.sentry.io/4509203143327744",
    environment: environment,
    ignoreErrors: [
      "TypeError: Load failed",
      "AxiosError: Network Error",
      "AxiosError: Request aborted",
    ],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
      Sentry.replayIntegration(),
    ],
    beforeSend(event) {
      if (event.level !== "error") {
        return null;
      }
      return event;
    },

    tracesSampleRate: environment === "production" ? 0.01 : 0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}
root.render(
  <ThemeProvider theme={darkTheme}>
    <App />
  </ThemeProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
